import type { ComboConfiguration } from "@/components/combo-configuration-cell"

export interface ApiOrderLike {
  id?: string | number
  client?: {
    id?: string | number
    company_name?: string
    first_name?: string
    last_name?: string
    email?: string | null
  }
  /** Client-role users linked to the order (e.g. nested on client_order list/detail) */
  users?: Array<{ email?: string | null }>
  businesses?: Array<{ id?: string | number; name?: string }>
  /** Nested business on list/detail when `businesses` is empty (same shape as order detail). */
  business?: { id?: string | number; name?: string; business_name?: string; company_name?: string } | null
  connections?: any[]
  anchors_texts?: any[]
  target_urls?: any[]
  recurring_monthly?: boolean
  recurring_type?: number
  starts_at?: string | null
  date_order?: string
  created_date?: string
  due_date?: string
  stage?: string | number
  status?: number | string | null
  is_paid?: boolean
  stripe_invoice?: string
  link_count?: number
  completed_links?: number
  [key: string]: any
}

// ─── New client_order status (0-4) ────────────────────────────────────────────

export const CLIENT_ORDER_STATUS_LABELS: Record<number, string> = {
  0: "Created",
  1: "In Progress",
  2: "Paused",
  3: "Cancelled",
  4: "Complete",
}

const CLIENT_ORDER_STATUS_COLORS: Record<number, string> = {
  0: "bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-50",
  1: "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-50",
  2: "bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-50",
  3: "bg-red-50 text-red-700 border-red-200 hover:bg-red-50",
  4: "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50",
}

// Maps old stage string key → new status integer
const STAGE_KEY_TO_STATUS: Record<string, number> = {
  paused: 2, added: 0, add_opps: 1, client_review: 1,
  fulfillment: 1, complete: 4, cancelled: 3, created: 0, in_progress: 1,
}

// ─── STAGE_NUM_TO_KEY ────────────────────────────────────────────────────────

const STAGE_NUM_TO_KEY: Record<number, string> = {
  0: "paused",
  1: "added",
  2: "add_opps",
  3: "client_review",
  4: "fulfillment",
  5: "complete",
  6: "cancelled",
  7: "created",
  8: "in_progress",
}

export const ORDER_STAGE_LABELS: Record<string, string> = {
  paused: "Paused",
  added: "Added",
  add_opps: "Add Opps",
  client_review: "Client Review",
  fulfillment: "Fulfillment",
  complete: "Completed",
  cancelled: "Cancelled",
  created: "Created",
  in_progress: "In Progress",
}

const statusColors: Record<string, string> = {
  draft: "bg-slate-100 text-slate-800 border-slate-200 hover:bg-slate-100",
  pending: "bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-50",
  "in-progress": "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-50",
  active: "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-50",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50",
  complete: "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50",
  created: "bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-50",
  cancelled: "bg-red-50 text-red-700 border-red-200 hover:bg-red-50",
  paused: "bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-50",
  added: "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-50",
  add_opps: "bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-50",
  client_review: "bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-50",
  fulfillment: "bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-50",
  in_progress: "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-50",
}

export function formatOrderTableDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

function formatMonthYear(dateString: string | null | undefined): string {
  if (dateString == null || dateString === "") return "—"
  const d = new Date(dateString)
  if (Number.isNaN(d.getTime())) return "—"
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" })
}

function formatQuarterYear(dateString: string | null | undefined): string {
  if (dateString == null || dateString === "") return "—"
  const d = new Date(dateString)
  if (Number.isNaN(d.getTime())) return "—"
  const month = d.getUTCMonth()
  const year = d.getUTCFullYear()
  const quarter = Math.floor(month / 3) + 1
  return `Q${quarter} ${year}`
}

export function getRecurrenceDisplay(order: ApiOrderLike): { periodLabel: string; frequencyLabel: string } | null {
  if (!order.recurring_monthly) return null
  const type = order.recurring_type
  const frequencyLabel = type === 2 ? "Quarter" : "Monthly"
  const isOriginal = order.starts_at == null || order.starts_at === ""
  const dateStr = isOriginal ? (order.date_order || order.created_date) : order.starts_at
  const periodLabel = type === 2 ? formatQuarterYear(dateStr) : formatMonthYear(dateStr)
  return { periodLabel, frequencyLabel }
}

export function safeNumericValue(value: any): number {
  if (value === null || value === undefined || value === "") return 0
  const num = Number(value)
  return isNaN(num) ? 0 : num
}

export function safeStringValue(value: any): string {
  if (value === null || value === undefined) return ""
  return String(value)
}

export function calculateProgress(complete: number, total: number): number {
  if (total <= 0) return 0
  return Math.min(100, Math.round((complete / total) * 100))
}

export function isConnectionDeleted(conn: any): boolean {
  if (!conn) return true
  const deleted = conn.deleted
  const deletedDate = conn.deleted_date ?? conn.date_deleted
  if (deleted != null && deleted !== undefined && deleted !== false && deleted !== 0 && deleted !== "") return true
  if (deletedDate != null && deletedDate !== undefined && String(deletedDate).trim() !== "") return true
  return false
}

const STATUS_STRING_TO_NUM: Record<string, number> = {
  created: 0,
  pending_client_approval: 1,
  approved_by_client: 2,
  complete: 3,
  completed: 3,
  fulfilled: 3,
  declined_by_client: 4,
  bad_apple: 5,
  reserve: 6,
}

function getConnectionStatusValue(conn: any): number | null {
  const s = conn?.status
  if (s === undefined || s === null || s === "" || String(s) === "n/a") return null
  if (typeof s === "number" && s >= 0 && s <= 6) return s
  const str = String(s).toLowerCase().trim()
  const fromMap = STATUS_STRING_TO_NUM[str]
  if (fromMap !== undefined) return fromMap
  const n = Number.parseInt(str, 10)
  return !Number.isNaN(n) && n >= 0 && n <= 6 ? n : null
}

function isConnectionExcludedFromProgress(conn: any): boolean {
  const connectionStatus = getConnectionStatusValue(conn)
  if (connectionStatus === null) return false
  return connectionStatus === 4 || connectionStatus === 5 || connectionStatus === 6
}

export function getLinksProgressCounts(order: ApiOrderLike): { linkCount: number; completeCount: number } {
  let linkCount = 0
  let completeCount = 0
  if (order.connections && Array.isArray(order.connections)) {
    const activeConnections = order.connections.filter((conn: any) => !isConnectionDeleted(conn))
    const countableConnections = activeConnections.filter((conn: any) => !isConnectionExcludedFromProgress(conn))
    linkCount = countableConnections.length
    completeCount = countableConnections.filter((conn: any) => {
      const connectionStatus = getConnectionStatusValue(conn)
      return connectionStatus === 3
    }).length
  }
  return { linkCount, completeCount }
}

function normalizeStageToKey(stage: string | number | null | undefined): string {
  if (stage === null || stage === undefined || stage === "") return "draft"
  const num = typeof stage === "number" ? stage : Number.parseInt(String(stage), 10)
  if (!Number.isNaN(num) && num in STAGE_NUM_TO_KEY) return STAGE_NUM_TO_KEY[num]
  const str = String(stage).toLowerCase().replace(/-/g, "_")
  return ORDER_STAGE_LABELS[str] ? str : str || "draft"
}

export function getStageStatusColor(stage: string | number | null | undefined): string {
  const key = normalizeStageToKey(stage)
  return statusColors[key] ?? statusColors.draft
}

export function formatStageDisplayName(stage: string | number | null | undefined): string {
  const key = normalizeStageToKey(stage)
  return ORDER_STAGE_LABELS[key] ?? (key === "draft" ? "Draft" : key.charAt(0).toUpperCase() + key.slice(1).replace(/[-_]/g, " "))
}

// ─── New status-aware display helpers ────────────────────────────────────────
// Prefer new status (0-4). When null/out-of-range, derive from stage.

// Django serializer returns status as label string (e.g. "in_progress"), not integer
const STATUS_LABEL_TO_NUM: Record<string, number> = {
  created: 0,
  in_progress: 1,
  paused: 2,
  cancelled: 3,
  complete: 4,
}

function resolveStatus(
  status: number | string | null | undefined,
  stage: string | number | null | undefined,
): number {
  const n = typeof status === "number" ? status : status != null ? Number(status) : NaN
  if (!Number.isNaN(n) && n >= 0 && n <= 4) return n
  if (typeof status === "string") {
    const fromLabel = STATUS_LABEL_TO_NUM[status.toLowerCase()]
    if (fromLabel !== undefined) return fromLabel
  }
  const key = normalizeStageToKey(stage ?? status)
  return STAGE_KEY_TO_STATUS[key] ?? 1
}

export function formatClientOrderStatus(
  status: number | string | null | undefined,
  stage?: string | number | null | undefined,
): string {
  return CLIENT_ORDER_STATUS_LABELS[resolveStatus(status, stage)] ?? "In Progress"
}

export function getClientOrderStatusColor(
  status: number | string | null | undefined,
  stage?: string | number | null | undefined,
): string {
  return CLIENT_ORDER_STATUS_COLORS[resolveStatus(status, stage)] ?? CLIENT_ORDER_STATUS_COLORS[1]
}

export function getDateStatus(dueDate: string): {
  status: "normal" | "overdue" | "approaching"
  daysOverdue?: number
  daysRemaining?: number
} {
  if (!dueDate) return { status: "normal" }
  const today = new Date()
  const due = new Date(dueDate)
  const diffTime = due.getTime() - today.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays < 0) {
    return { status: "overdue", daysOverdue: Math.abs(diffDays) }
  }
  if (diffDays <= 7) {
    return { status: "approaching", daysRemaining: diffDays }
  }
  return { status: "normal" }
}

export function getOrderLocations(order: ApiOrderLike): string[] {
  const locations: string[] = []
  const locationSet = new Set<string>()

  if (order.combos && Array.isArray(order.combos) && order.combos.length > 0) {
    order.combos.forEach((combo: any) => {
      const location = formatAddressLocation(combo.address)
      if (location && !locationSet.has(location)) {
        locationSet.add(location)
        locations.push(location)
      }
    })
    if (locations.length > 0) return locations
  }

  if (order.anchors_texts && Array.isArray(order.anchors_texts)) {
    order.anchors_texts.forEach((anchorText: any) => {
      if (anchorText.address) {
        const address = anchorText.address
        const parts: string[] = []
        if (address.city) parts.push(address.city)
        if (address.state) parts.push(address.state)
        if (address.zip_code && address.zip_code.length <= 2) parts.push(address.zip_code)
        const location = parts.length > 0 ? parts.join(", ").trim() : ""
        if (location && !locationSet.has(location)) {
          locationSet.add(location)
          locations.push(location)
        }
      }
    })
  }

  if (locations.length === 0 && order.target_urls && Array.isArray(order.target_urls)) {
    order.target_urls.forEach((targetUrl: any) => {
      if (targetUrl.location) {
        const location =
          typeof targetUrl.location === "object"
            ? `${targetUrl.location.city || ""}, ${targetUrl.location.state || ""}`.trim().replace(/^,\s*|,\s*$/g, "")
            : targetUrl.location
        if (location && !locationSet.has(location)) {
          locationSet.add(location)
          locations.push(location)
        }
      }
    })
  }

  return locations
}

function countLinksFulfilledForCombo(
  connections: any[],
  targetUrlText: string,
  anchorTextValue: string
): number {
  if (!connections || !Array.isArray(connections)) return 0
  return connections.filter((conn: any) => {
    const connTargetUrlText = conn?.combo?.target_url_text ?? ""
    const connAnchorTextValue = conn?.combo?.anchor_text_value ?? ""
    return connTargetUrlText === targetUrlText && connAnchorTextValue === anchorTextValue
  }).length
}

function formatAddressLocation(address: any): string {
  if (!address) return ""
  const parts: string[] = []
  if (address.city) parts.push(address.city)
  if (address.state) parts.push(address.state)
  if (address.zip_code && address.zip_code.length <= 2) parts.push(address.zip_code)
  return parts.length > 0 ? parts.join(", ").trim() : ""
}

export function getOrderCombos(order: ApiOrderLike): ComboConfiguration[] {
  const combos: ComboConfiguration[] = []

  if (order.combos && Array.isArray(order.combos) && order.combos.length > 0) {
    order.combos.forEach((combo: any) => {
      const targetUrlText = combo.target_url_text || ""
      const anchorTextValue = combo.anchor_text_value || ""
      const linksNeeded = combo.links_needed ?? 1
      const location = formatAddressLocation(combo.address)
      const linksFulfilled = countLinksFulfilledForCombo(order.connections || [], targetUrlText, anchorTextValue)
      combos.push({
        targetUrl: targetUrlText,
        anchorText: anchorTextValue,
        location,
        linksNeeded,
        linksFulfilled,
      })
    })
    return combos
  }

  if (order.anchors_texts && Array.isArray(order.anchors_texts)) {
    order.anchors_texts.forEach((anchorText: any) => {
      const anchorTextValue = anchorText.value || ""
      const targetUrl = anchorText.target_url
      const targetUrlText =
        targetUrl && typeof targetUrl === "object" ? (targetUrl.text || targetUrl.url || "") : (targetUrl || "")
      const location = formatAddressLocation(anchorText.address)
      const linksFulfilled = countLinksFulfilledForCombo(order.connections || [], targetUrlText, anchorTextValue)
      combos.push({
        targetUrl: targetUrlText || "",
        anchorText: anchorTextValue || "",
        location: location || "",
        linksNeeded: 1,
        linksFulfilled,
      })
    })
  }

  if (combos.length === 0 && order.target_urls && Array.isArray(order.target_urls)) {
    order.target_urls.forEach((targetUrl: any) => {
      const targetUrlText = typeof targetUrl === "object" ? (targetUrl.text || targetUrl.url || "") : targetUrl
      const location =
        typeof targetUrl.location === "object"
          ? `${targetUrl.location.city || ""}, ${targetUrl.location.state || ""}`.trim().replace(/^,\s*|,\s*$/g, "")
          : (targetUrl.location || "")

      const anchorTexts = targetUrl.anchor_texts || []
      if (anchorTexts.length > 0) {
        anchorTexts.forEach((anchorText: any) => {
          const anchorTextValue = typeof anchorText === "object" ? (anchorText.value || anchorText.text || "") : anchorText
          let linksFulfilled = 0
          if (order.connections && Array.isArray(order.connections)) {
            linksFulfilled = order.connections.filter((conn: any) => {
              const connTargetUrlText = conn?.combo?.target_url_text ?? ""
              const connAnchorTextValue = conn?.combo?.anchor_text_value ?? ""
              return connTargetUrlText === targetUrlText && connAnchorTextValue === anchorTextValue
            }).length
          }
          combos.push({ targetUrl: targetUrlText || "", anchorText: anchorTextValue || "", location: location || "", linksNeeded: 1, linksFulfilled })
        })
      } else {
        combos.push({ targetUrl: targetUrlText || "", anchorText: "", location: location || "", linksNeeded: 1, linksFulfilled: 0 })
      }
    })
  }

  return combos
}

export function getLinkStatuses(order: ApiOrderLike) {
  const statuses = {
    created: 0,
    pendingClientApproval: 0,
    approvedByClient: 0,
    complete: 0,
    declinedByClient: 0,
    badApple: 0,
    live: 0,
    reserves: 0,
  }

  if (order.connections && Array.isArray(order.connections)) {
    const activeConnections = order.connections.filter((conn: any) => !isConnectionDeleted(conn))
    activeConnections.forEach((conn: any) => {
      const status = getConnectionStatusValue(conn)
      const linkCheckerStatus = conn?.url_check?.status || ""
      const statusLower = (linkCheckerStatus || "").toLowerCase()
      if (statusLower === "active" || statusLower.includes("live")) statuses.live++

      if (status === null || status === 0) {
        statuses.created++
      } else if (status === 1) {
        statuses.pendingClientApproval++
      } else if (status === 2) {
        statuses.approvedByClient++
      } else if (status === 3) {
        statuses.complete++
      } else if (status === 4) {
        statuses.declinedByClient++
      } else if (status === 5) {
        statuses.badApple++
      } else if (status === 6) {
        statuses.reserves++
      } else {
        statuses.created++
      }
    })
  }

  return statuses
}

export function getClientName(order: ApiOrderLike): string {
  if (order.client?.company_name) return order.client.company_name
  if (order.client?.first_name || order.client?.last_name) {
    return `${order.client?.first_name || ""} ${order.client?.last_name || ""}`.trim()
  }
  return "Unknown Client"
}

function businessNameFromObject(b: { name?: string; business_name?: string; company_name?: string } | null | undefined): string | null {
  if (!b || typeof b !== "object") return null
  const n = b.name ?? b.business_name ?? b.company_name
  if (n == null || String(n).trim() === "") return null
  return String(n)
}

/** Same resolution as order detail: `businesses[0]`, else nested `business.name`. */
export function getBusinessName(order: ApiOrderLike): string | null {
  if (order.businesses && order.businesses.length > 0) {
    const fromFirst = businessNameFromObject(order.businesses[0])
    if (fromFirst) return fromFirst
  }
  return businessNameFromObject(order.business ?? null)
}

export function getClientId(order: ApiOrderLike): string | null {
  if (order.client?.id) return order.client.id.toString()
  return null
}

export function getBusinessId(order: ApiOrderLike): string | null {
  if (order.businesses && order.businesses.length > 0) {
    const id = order.businesses[0].id
    if (id != null && String(id) !== "") return String(id)
  }
  const b = order.business
  if (b != null && typeof b === "object" && !Array.isArray(b) && b.id != null && String(b.id) !== "") {
    return String(b.id)
  }
  return null
}

/** Comma-separated emails from `order.users` only (no other source). */
export function getOrderLinkedUsersEmails(order: ApiOrderLike): string {
  const raw = order.users
  if (!Array.isArray(raw)) return ""
  const emails: string[] = []
  for (const u of raw) {
    if (!u || typeof u !== "object") continue
    const e = (u as { email?: unknown }).email
    if (typeof e === "string") {
      const t = e.trim()
      if (t) emails.push(t)
    }
  }
  return emails.join(", ")
}
